import { RootStore } from '../stores/root';
import {
  findRequestedAccount,
  getRequestedOrganizationId,
} from './requestedOrganization';

/**
 * Resolves whether the account matching `email` mandates SSO login and, if so,
 * triggers the SSO redirect.
 *
 * Fetches the user's accounts via the `userAccountsByEmail` RPC, picks the
 * relevant account (by the URL's organizationId or the `parentHint` query
 * param, when multiple accounts match), and redirects to SSO when the account
 * has an SSO URL and either SSO is mandatory or the account has no password
 * authenticator.
 *
 * @returns true if an SSO redirect was triggered, false otherwise (including
 *          when the lookup fails, so callers can fall back to their default
 *          behavior).
 */
export const handleSsoRedirect = async (
  rootStore: RootStore,
  email: string,
): Promise<boolean> => {
  try {
    const response = await rootStore.apiStore.getUserAccounts({ email });
    const accounts = response?.accountsData;
    if (!accounts?.length) {
      return false;
    }
    const { navigationStore } = rootStore;
    const account =
      (accounts.length > 1 &&
        findRequestedAccount(accounts, {
          organizationId: getRequestedOrganizationId(rootStore),
          parentHint: navigationStore.getQueryParam('parentHint'),
        })) ||
      accounts[0];
    const ssoUrl = account?.accountSsoSettings?.ssoUrl;
    const isSsoMandatory =
      ssoUrl &&
      (account?.accountSsoSettings?.isSsoMandatory ||
        !account?.userAuthenticatorsData?.hasPassword);

    if (isSsoMandatory) {
      rootStore.ssoStore.redirectSsoUrl({ email, ssoUrl });
      return true; // SSO redirect happened
    }
    return false; // No SSO redirect needed
  } catch (error) {
    // If the SSO check fails, let the caller fall back to its default behavior.
    return false;
  }
};
